<template>
  <div
    ref="container"
    class="scrollbar-container"
    :class="{ horizontal: horizontal, mobile: isMobile }"
  >
    <div ref="scrollerWrapper" class="scroller-wrapper">
      <slot></slot>
    </div>

    <div
      v-if="horizontal"
      ref="scrollbarX"
      key="scrollbarX"
      class="scrollbar-x"
    >
      <div ref="thumbX" class="thumb-x"></div>
    </div>
    <div v-if="vertical" ref="scrollbarY" key="scrollbarY" class="scrollbar-y">
      <div ref="thumbY" class="thumb-y"></div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { debounce } from '@/utils/debounce'

const detectMobile = (): boolean => {
  return typeof window.orientation !== 'undefined'
}

defineOptions({ name: 'app-Scrollbar' })

const props = withDefaults(
  defineProps<{
    horizontalScroll?: boolean
    verticalScroll?: boolean
  }>(),
  {
    horizontalScroll: undefined,
    verticalScroll: true,
  },
)

const emit = defineEmits<{
  onScroll: [position: { x: number; y: number }]
}>()

// Template refs
const container = ref<HTMLElement | null>(null)
const scrollerWrapper = ref<HTMLElement | null>(null)
const scrollbarX = ref<HTMLElement | null>(null)
const scrollbarY = ref<HTMLElement | null>(null)
const thumbX = ref<HTMLElement | null>(null)
const thumbY = ref<HTMLElement | null>(null)

// State
const scroller = ref<HTMLElement | null>(null)
const horizontal = ref(false)
const vertical = ref(true)
const childrenWidth = ref(0)
const draggingX = ref(false)
const draggingY = ref(false)
const isScrolling = ref<number | undefined>(undefined)
const isMobile = ref(detectMobile())
const mouseMoveEv = ref<((event: MouseEvent | TouchEvent) => void) | null>(null)
const debouncedUpdate = ref<((event: Event) => void) | undefined>(undefined)
const isUnmounted = ref(false)

onMounted(() => {
  scroller.value =
    (scrollerWrapper.value?.firstElementChild as HTMLElement | null) ?? null
  vertical.value = props.verticalScroll

  if (!scroller.value) return

  calcChildrenWidth()

  if (
    childrenWidth.value > scroller.value.offsetWidth &&
    props.horizontalScroll !== false
  ) {
    horizontal.value = true
  }

  nextTick(() => {
    if (isUnmounted.value) {
      return
    }
    addEventListeners()

    if (horizontal.value) resizeScrollbarX()
    if (vertical.value) resizeScrollbarY()
  })
})

onBeforeUnmount(() => {
  isUnmounted.value = true
  if (mouseMoveEv.value) {
    clearMouseMoveEv()
  }
  if (thumbX.value) {
    thumbX.value.removeEventListener('mousedown', onThumbXMouseDown)
    thumbX.value.removeEventListener('touchstart', onThumbXMouseDown)
  }
  if (thumbY.value) {
    thumbY.value.removeEventListener('mousedown', onThumbYMouseDown)
    thumbY.value.removeEventListener('touchstart', onThumbYMouseDown)
  }

  scroller.value?.removeEventListener('scroll', handleScroll)
  if (debouncedUpdate.value) {
    window.removeEventListener('resize', debouncedUpdate.value, false)
  }
  window.removeEventListener('mouseup', onThumbDragStop)
  window.removeEventListener('touchend', onThumbDragStop)
})

function addEventListeners(): void {
  if (horizontal.value && thumbX.value) {
    thumbX.value.addEventListener('mousedown', onThumbXMouseDown)
    thumbX.value.addEventListener('touchstart', onThumbXMouseDown)
  }
  if (vertical.value && thumbY.value) {
    thumbY.value.addEventListener('mousedown', onThumbYMouseDown)
    thumbY.value.addEventListener('touchstart', onThumbYMouseDown)
  }

  scroller.value?.addEventListener('scroll', handleScroll)
  debouncedUpdate.value = debounce(update, 360)
  window.addEventListener('resize', debouncedUpdate.value, false)
  window.addEventListener('mouseup', onThumbDragStop)
  window.addEventListener('touchend', onThumbDragStop)
}

function handleScroll(): void {
  if (!container.value || !scroller.value) return

  const lastX = (scroller.value.scrollLeft / scroller.value.scrollWidth) * 100
  const lastY = (scroller.value.scrollTop / scroller.value.scrollHeight) * 100

  container.value.classList.add('scrolling')
  window.clearTimeout(isScrolling.value)
  isScrolling.value = window.setTimeout(() => {
    if (!container.value?.classList) {
      console.warn(
        'Trying to remove class scrolling but no classList is available',
      )
    } else {
      container.value.classList.remove('scrolling')
    }
  }, 66)

  window.requestAnimationFrame(() => {
    if (!scroller.value) return
    if (horizontal.value) moveScrollbarX(lastX)
    if (vertical.value) moveScrollbarY(lastY)
    emit('onScroll', {
      x: scroller.value.scrollLeft,
      y: scroller.value.scrollTop,
    })
  })
}

function moveScrollbarX(newX: number): void {
  if (!thumbX.value) return
  thumbX.value.style.left = `${newX}%`
}

function moveScrollbarY(newY: number): void {
  if (!thumbY.value) return
  thumbY.value.style.top = `${newY}%`
}

function resizeScrollbarX(): void {
  if (
    !container.value ||
    !scroller.value ||
    !thumbX.value ||
    !scrollbarX.value
  ) {
    return
  }

  const percentWidth = container.value.offsetWidth / scroller.value.scrollWidth
  const width = container.value.offsetWidth * percentWidth
  thumbX.value.style.width = `${width}px`

  if (childrenWidth.value <= container.value.offsetWidth) {
    scrollbarX.value.style.display = 'none'
  } else {
    scrollbarX.value.style.display = 'inherit'
  }
}

function resizeScrollbarY(): void {
  if (
    !container.value ||
    !scroller.value ||
    !thumbY.value ||
    !scrollbarY.value
  ) {
    return
  }

  const percentHeight =
    container.value.offsetHeight / scroller.value.scrollHeight
  const height = container.value.offsetHeight * percentHeight
  thumbY.value.style.height = `${height}px`

  if (scroller.value.scrollHeight <= container.value.offsetHeight) {
    scrollbarY.value.style.display = 'none'
  } else {
    scrollbarY.value.style.display = 'inherit'
  }
}

function onThumbXMouseDown(event: MouseEvent | TouchEvent): void {
  if (!container.value || !thumbX.value) return

  draggingX.value = true
  container.value.classList.add('dragging')

  const left = thumbX.value.style.left ? thumbX.value.style.left : '0%'
  const perc = parseFloat(left.slice(0, -1)) / 100
  const posX = container.value.offsetWidth * perc
  const clientX =
    'clientX' in event && (event.clientX || event.clientX === 0)
      ? event.clientX
      : (event as TouchEvent).touches[0].clientX
  const offset = clientX - posX

  if (mouseMoveEv.value) {
    clearMouseMoveEv()
  }
  mouseMoveEv.value = (moveEvent: MouseEvent | TouchEvent) =>
    onThumbXDragStart(offset, moveEvent)

  window.addEventListener('mousemove', mouseMoveEv.value)
  window.addEventListener('touchmove', mouseMoveEv.value)

  event.stopPropagation()
}

function onThumbYMouseDown(event: MouseEvent | TouchEvent): void {
  if (!container.value || !thumbY.value) return

  draggingY.value = true
  container.value.classList.add('dragging')

  const top = thumbY.value.style.top ? thumbY.value.style.top : '0%'
  const perc = parseFloat(top.slice(0, -1)) / 100
  const posY = container.value.offsetHeight * perc
  const clientY =
    'clientY' in event && (event.clientY || event.clientY === 0)
      ? event.clientY
      : (event as TouchEvent).touches[0].clientY
  const offset = clientY - posY

  mouseMoveEv.value = (moveEvent: MouseEvent | TouchEvent) =>
    onThumbYDragStart(offset, moveEvent)

  window.addEventListener('mousemove', mouseMoveEv.value)
  window.addEventListener('touchmove', mouseMoveEv.value)

  event.stopPropagation()
}

function onThumbXDragStart(
  offset: number,
  event: MouseEvent | TouchEvent,
): void {
  if (!container.value || !scroller.value) return
  if (draggingX.value && offset) {
    const clientX =
      'clientX' in event ? event.clientX : event.touches[0].clientX
    const perc = (clientX - offset) / container.value.offsetWidth
    const posX = scroller.value.scrollWidth * perc
    scroller.value.scrollLeft = posX
  }
}

function onThumbYDragStart(
  offset: number,
  event: MouseEvent | TouchEvent,
): void {
  if (!container.value || !scroller.value) return
  if (draggingY.value && offset) {
    const clientY =
      'clientY' in event ? event.clientY : event.touches[0].clientY
    const perc = (clientY - offset) / container.value.offsetHeight
    const posY = scroller.value.scrollHeight * perc
    scroller.value.scrollTop = posY
  }
}

function onThumbDragStop(): void {
  draggingX.value = false
  draggingY.value = false
  container.value && container.value.classList.remove('dragging')
  clearMouseMoveEv()
}

function clearMouseMoveEv(): void {
  if (mouseMoveEv.value) {
    window.removeEventListener('mousemove', mouseMoveEv.value)
    window.removeEventListener('touchmove', mouseMoveEv.value)
  }
  mouseMoveEv.value = null
}

function calcChildrenWidth(): void {
  if (!scroller.value) return
  Array.from(scroller.value.children).forEach((child) => {
    const element = child as HTMLElement
    if (childrenWidth.value <= element.offsetWidth) {
      childrenWidth.value = element.offsetWidth
    }
  })
}

/**
 * @public
 */
function update(): void {
  if (!container.value) return

  calcChildrenWidth()

  if (horizontal.value) resizeScrollbarX()
  if (vertical.value) resizeScrollbarY()

  handleScroll()
}

/**
 * @public
 */
function scrollTo(x = 0, y = 0): void {
  scroller.value?.scrollTo(x, y)
}

defineExpose({ update, scrollTo })
</script>
<style scoped lang="scss" src="./scrollbar.scss"></style>
